<template>
  <div class="card p-3 mb-4">
    <h5><i class="bi bi-credit-card-2-front me-2"></i>Formas de Pago</h5>
    <p class="text-muted">Configure cómo se realizará el cobro</p>

    <!-- Cobro en efectivo: el cajero carga el billete y ve el vuelto sin ir al
         servidor (SPEC_COBRO_PERFORMANCE_v0.1.md, RF-6). -->
    <div v-if="cashReceivedMode" class="cash-received-panel mb-3">
      <label class="form-label fw-semibold mb-1" for="cashReceivedInput">
        ¿Con cuánto paga?
      </label>
      <div class="input-group input-group-lg mb-2">
        <span class="input-group-text">₲</span>
        <!-- RF-7: separador de miles. El `step="500"` se pierde al dejar de ser
             `type="number"` (un input numérico no admite separadores); los
             botones de billete rápido de abajo cubren ese atajo. -->
        <MoneyInput
          input-id="cashReceivedInput"
          ref="cashReceivedInput"
          v-model="cashReceived"
          :placeholder="String(Math.round(total))"
          @update:model-value="applyCashReceived"
          @keydown.enter.prevent="$emit('submit-request')"
        />
      </div>

      <div class="quick-bills mb-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="setCashReceived(total)"
        >
          Importe exacto
        </button>
        <button
          v-for="bill in quickBills"
          :key="bill"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="setCashReceived(bill)"
        >
          {{ formatBill(bill) }}
        </button>
      </div>

      <div
        class="change-line"
        :class="summary.sufficient ? 'change-line--ok' : 'change-line--missing'"
      >
        <span>{{ summary.sufficient ? 'Vuelto' : 'Falta' }}</span>
        <strong>{{ formatCurrency(summary.sufficient ? summary.change : summary.remaining) }}</strong>
      </div>
    </div>

    <div
      v-if="enforceTotal && summary.invalidChangeMethods.length"
      class="alert alert-danger py-2 px-3 mb-3"
      role="alert"
    >
      <i class="bi bi-exclamation-triangle me-1"></i>
      No se puede dar vuelto en {{ summary.invalidChangeMethods.join(' / ').toLowerCase() }}.
      En un pago mixto el total tiene que ser exacto; el excedente solo puede ir en efectivo o cheque.
    </div>

    <div :class="`${bgVariant} p-3 rounded mb-3`">
      <div class="d-flex justify-content-between">
        <div>Monto Total:</div>
        <div class="fw-bold">{{ formatCurrency(total) }}</div>
      </div>
      <div class="d-flex justify-content-between">
        <div>Total Asignado:</div>
        <div class="fw-bold">{{ formatCurrency(assigned) }}</div>
      </div>
      <!-- En modo cobro en efectivo esta línea ya está arriba, en grande. -->
      <div v-if="!cashReceivedMode" class="d-flex justify-content-between">
        <div>{{ labelRemaining }}:</div>
        <div :class="remaining > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'">
          {{ formatCurrency(remainingAbsolute) }}
        </div>
      </div>
    </div>

    <hr />

    <div class="row g-2 align-items-center mb-3">
      <div class="col-md-4">
        <select v-model="type" class="form-select" :disabled="hasCredito">
          <option disabled value="">Tipo</option>
          <option v-for="t in types" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <div class="col-md-4">
        <MoneyInput
          ref="amountInput"
          v-model="amount"
          placeholder="Monto"
          :disabled="hasCredito"
          @keydown.enter.prevent="handleEnterAmount"
        />
      </div>
      <div class="col-md-4 d-grid">
        <button class="btn btn-dark" :disabled="!canAdd" @click="add">
          <i class="bi bi-plus-lg me-1"></i>Agregar
        </button>
      </div>
    </div>

    <div v-if="hasCredito" class="alert alert-warning">
      <small><i class="bi bi-info-circle me-1"></i>Modo Crédito: No se pueden agregar otros métodos de pago.</small>
    </div>

    <ul class="list-group list-group-flush mt-3">
      <li
        v-for="(p, i) in payments"
        :key="i"
        class="list-group-item d-flex justify-content-between align-items-center"
      >
        <div>{{ p.type }}</div>
        <div class="d-flex align-items-center">
          <div class="me-3">{{ formatCurrency(p.amount) }}</div>
          <button class="btn btn-outline-danger btn-sm" @click="removePayment(i)">
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { summarizePayments, suggestBills } from '@/utils/cashPayments.mjs'
import MoneyInput from '@/components/MoneyInput.vue'

export default {
  name: 'PaymentForms',
  components: { MoneyInput },
  props: {
    total: {
      type: Number,
      required: true
    },
    types: {
      type: Array,
      default: () => ['Efectivo', 'Tarjeta', 'Cheque', 'Transferencia']
    },
    payments: {
      type: Array,
      default: () => []
    },
    variant: {
      type: String,
      default: 'income'    // 'income' o 'expense'
    },
    // Ambas en `false` por defecto: las pantallas de egreso y pago a proveedor
    // siguen comportándose exactamente igual que antes (RF-9).
    cashReceivedMode: {
      type: Boolean,
      default: false
    },
    enforceTotal: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:payments', 'update:summary', 'submit-request'],
  data() {
    return { type: '', amount: null, cashReceived: null }
  },
  mounted() {
    this.initDefaultType()
    if (this.cashReceivedMode) {
      // Arranca con lo que el padre haya precargado en efectivo (habitualmente
      // el importe exacto), para no hacer más lento el caso de pago justo.
      const efectivo = this.payments.find((p) => this.isCash(p))
      this.cashReceived = efectivo ? Number(efectivo.amount) : null
      this.$nextTick(() => this.$refs.cashReceivedInput?.focus())
    }
    this.$emit('update:summary', this.summary)
  },
  watch: {
    types: {
      handler(newVal) {
        // Si el tipo actual no existe en la lista o está vacío, reinicializar
        if (!this.type || !newVal.includes(this.type)) {
          this.initDefaultType()
        }
      },
      deep: false
    },
    summary: {
      handler(value) {
        this.$emit('update:summary', value)
      },
      deep: true
    },
    // Si el efectivo cambia por otra vía (agregar/quitar una forma de pago a
    // mano), el campo de billete recibido tiene que reflejarlo.
    payments: {
      handler(nuevo) {
        if (!this.cashReceivedMode) return
        const enEfectivo = nuevo
          .filter((p) => this.isCash(p))
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
        if (enEfectivo !== Number(this.cashReceived)) {
          this.cashReceived = enEfectivo > 0 ? enEfectivo : null
        }
      },
      deep: true
    }
  },
  computed: {
    // Espejo de las reglas del backend; ver `@/utils/cashPayments.mjs`.
    summary() {
      return summarizePayments({ payments: this.payments, total: this.total })
    },
    quickBills() {
      return suggestBills({ total: this.total })
    },
    assigned() {
      return this.payments.reduce((sum, p) => sum + p.amount, 0)
    },
    bgVariant() {
     return this.variant === 'expense'
       ? 'bg-danger bg-opacity-10'
       : 'bg-success bg-opacity-10'
    },
    remaining() {
      return this.total - this.assigned
    },
    hasCredito() {
      return this.payments.some(p => (p.type || '').toUpperCase() === 'CREDITO')
    },
    canAdd() {
      // Si ya hay Crédito, no se puede agregar nada más
      if (this.hasCredito) return false
      // Si se está intentando agregar Crédito, solo se permite si no hay otros pagos
      if ((this.type || '').toUpperCase() === 'CREDITO' && this.payments.length > 0) return false
      return Boolean(this.type) && Number(this.amount) > 0
    },
    labelRemaining() {
      return this.remaining >= 0 ? 'Restante' : 'Vuelto'
    },
       
    remainingAbsolute() {
      return Math.abs(this.remaining)
    },
  },
  methods: {
    isCash(payment = {}) {
      return String(payment.type || '').trim().toUpperCase() === 'EFECTIVO'
    },
    /** Reemplaza la línea de efectivo por el importe recibido, sin tocar el resto. */
    applyCashReceived() {
      const monto = Number(this.cashReceived)
      const otros = this.payments.filter((p) => !this.isCash(p))
      const nuevo = Number.isFinite(monto) && monto > 0
        ? [...otros, { type: 'Efectivo', amount: monto }]
        : otros
      this.$emit('update:payments', nuevo)
    },
    setCashReceived(value) {
      this.cashReceived = Math.round(Number(value) || 0)
      this.applyCashReceived()
      this.$nextTick(() => this.$refs.cashReceivedInput?.focus())
    },
    formatBill(value) {
      return `${Math.round(value / 1000)} mil`
    },
    initDefaultType() {
      const list = Array.isArray(this.types) ? this.types : []
      if (list.includes('Efectivo')) {
        this.type = 'Efectivo'
      } else {
        this.type = list[0] || ''
      }
    },
    add() {
      const nuevo = [...this.payments, { type: this.type, amount: this.amount }]
      this.$emit('update:payments', nuevo)
      this.type = ''
      this.amount = null
      // Tras agregar, deja preseleccionado de nuevo 'Efectivo' (o primer tipo)
      this.initDefaultType()
    },
    handleEnterAmount() {
      if (!this.canAdd) return
      this.add()
      this.$nextTick(() => {
        this.$refs.amountInput?.focus()
      })
    },
    formatCurrency(val) {
      return new Intl.NumberFormat('es-PY', {
        style: 'currency',
        currency: 'PYG'
      }).format(Math.round(Number(val || 0)))
    },
    /** Elimina el pago en la posición indicada */
    removePayment(index) {
      const nuevo = [...this.payments]
      nuevo.splice(index, 1)
      this.$emit('update:payments', nuevo)
    },
  }
}
</script>


<style scoped>
.list-group-item {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.cash-received-panel {
  border: 1px solid #dbe3ec;
  border-radius: 0.5rem;
  padding: 0.9rem;
  background: #f8fbff;
}

.quick-bills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.change-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
  font-size: 1.05rem;
}

.change-line strong {
  font-size: 1.6rem;
  line-height: 1.1;
}

.change-line--ok {
  background: #e7f6ec;
  color: #0f5132;
}

.change-line--missing {
  background: #fdeaea;
  color: #842029;
}
</style>
